// Jun-2026 (owner-authorized) — Amend an APPROVED Project GRN instead of a
// full reversal. New quantities + header details flow the SAME project_grn
// Approval Matrix; the stock delta is applied ONLY on final approval.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, FileEdit, AlertTriangle, Lock } from "lucide-react";

const inputCls = "w-full border border-slate-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:border-[#F97316]";
const numCls = "w-20 border border-slate-300 rounded px-1.5 py-1 text-xs text-right focus:outline-none focus:border-[#F97316]";
const HEADS = [
  ["challan_no", "Challan No", "text"],
  ["challan_date", "Challan Date", "date"],
  ["vehicle_no", "Vehicle No", "text"],
  ["received_by", "Received By", "text"],
  ["quality_remarks", "Quality Remarks", "text"],
];

export default function GrnAmendModal({ project, grnId, onClose, onDone }) {
  const pid = project.id;
  const [ctx, setCtx] = useState(null);
  const [head, setHead] = useState({});
  const [rows, setRows] = useState({});
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/projects/${pid}/grns/${grnId}/amend-context`)
      .then((r) => {
        setCtx(r.data);
        setHead(r.data.header || {});
        const init = {};
        (r.data.lines || []).forEach((l) => {
          init[l.grn_line_uid] = {
            received_qty: l.received_qty, accepted_qty: l.accepted_qty,
            rejected_qty: l.rejected_qty, damaged_qty: l.damaged_qty,
          };
        });
        setRows(init);
      })
      .catch((e) => { toast.error(e?.response?.data?.detail || "Failed to load GRN"); onClose(); });
    // eslint-disable-next-line
  }, [pid, grnId]);

  const setRow = (uid, patch) => setRows((r) => ({ ...r, [uid]: { ...r[uid], ...patch } }));

  const lineProblem = (l) => {
    const r = rows[l.grn_line_uid] || {};
    const rec = Number(r.received_qty || 0), acc = Number(r.accepted_qty || 0);
    const rej = Number(r.rejected_qty || 0), dam = Number(r.damaged_qty || 0);
    if (rec <= 0) return "received must be positive";
    if (acc < 0 || rej < 0 || dam < 0) return "quantities cannot be negative";
    if (Math.abs(acc + rej + dam - rec) > 0.001) return "accepted + rejected + damaged must equal received";
    if (Math.abs(acc - l.accepted_qty) > 0.001) {
      if (l.pi_locked) return "accepted qty frozen — a Purchase Invoice references this batch";
      if (l.batch_reversed) return "batch reversed — quantity cannot change";
      if (acc < l.min_accepted - 0.001) return `accepted cannot go below ${l.min_accepted} (consumed/reserved)`;
      if (acc > l.max_accepted + 0.001) return `accepted cannot exceed ${l.max_accepted} (approved PO remaining)`;
    }
    return "";
  };

  const anyChange = ctx && ((ctx.lines || []).some((l) => {
    const r = rows[l.grn_line_uid] || {};
    return ["received_qty", "accepted_qty", "rejected_qty", "damaged_qty"]
      .some((k) => Math.abs(Number(r[k] || 0) - l[k]) > 0.001);
  }) || HEADS.some(([k]) => String(head[k] ?? "") !== String((ctx.header || {})[k] ?? "")));

  const problems = ctx ? (ctx.lines || []).map(lineProblem).filter(Boolean) : [];

  const submit = async () => {
    if (!reason.trim()) { toast.error("Amendment reason is mandatory"); return; }
    if (problems.length) { toast.error(problems[0]); return; }
    const lines = (ctx.lines || []).map((l) => {
      const r = rows[l.grn_line_uid] || {};
      return {
        grn_line_uid: l.grn_line_uid,
        received_qty: Number(r.received_qty || 0), accepted_qty: Number(r.accepted_qty || 0),
        rejected_qty: Number(r.rejected_qty || 0), damaged_qty: Number(r.damaged_qty || 0),
      };
    });
    setSaving(true);
    try {
      const r = await api.post(`/projects/${pid}/grns/${grnId}/amend`, { reason, header: head, lines });
      toast.success(r.data?.auto_approved
        ? "Amendment applied — no approval matrix configured, stock updated"
        : "Amendment submitted for approval — stock changes apply only on final approval");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit amendment");
    } finally { setSaving(false); }
  };

  if (!ctx) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-8" data-testid="grn-amend-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="font-bold text-sm flex items-center gap-2">
            <FileEdit size={15} className="text-[#F97316]" /> Amend GRN <span className="font-mono">{ctx.grn_number}</span>
          </div>
          <button data-testid="grn-amend-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4 text-xs">
          {!ctx.amendable ? (
            <div className="border border-rose-300 bg-rose-50 rounded p-3 flex items-start gap-2" data-testid="grn-amend-blocked">
              <AlertTriangle size={14} className="text-rose-600 mt-0.5" />
              <div>{(ctx.reasons || []).join("; ")}</div>
            </div>
          ) : (
            <>
              <div className="border border-sky-200 bg-sky-50 rounded p-2.5 text-sky-900">
                Changes go through the <b>Approval Matrix</b> again — locked stock is adjusted only on final approval
                (append-only: compensating entries, originals retained).
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {HEADS.map(([k, label, type]) => (
                  <div key={k}>
                    <div className="text-[10px] uppercase text-slate-400 font-bold mb-0.5">{label}</div>
                    <input data-testid={`grn-amend-head-${k}`} type={type} className={inputCls}
                      value={head[k] ?? ""} onChange={(e) => setHead((h) => ({ ...h, [k]: e.target.value }))} />
                  </div>
                ))}
              </div>
              <div className="border border-slate-200 rounded overflow-x-auto">
                <table className="w-full min-w-[680px] text-xs" data-testid="grn-amend-lines-table">
                  <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                    <tr>
                      <th className="px-2 py-2 text-left">Item</th>
                      <th className="px-2 py-2 text-right">Received</th>
                      <th className="px-2 py-2 text-right">Accepted</th>
                      <th className="px-2 py-2 text-right">Rejected</th>
                      <th className="px-2 py-2 text-right">Damaged</th>
                      <th className="px-2 py-2 text-left">Limits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(ctx.lines || []).map((l) => {
                      const r = rows[l.grn_line_uid] || {};
                      const frozen = l.pi_locked || l.batch_reversed;
                      const prob = lineProblem(l);
                      return (
                        <tr key={l.grn_line_uid} className="border-t border-slate-100 align-top">
                          <td className="px-2 py-1.5">
                            {l.item_name} <span className="text-slate-400 font-mono">{l.item_code}</span>
                            <div className="text-[10px] text-slate-400">{l.uom} · was {l.received_qty}/{l.accepted_qty}/{l.rejected_qty}/{l.damaged_qty}</div>
                            {prob && <div className="text-[10px] text-rose-600 font-semibold mt-0.5" data-testid={`grn-amend-problem-${l.grn_line_uid}`}>{prob}</div>}
                          </td>
                          {["received_qty", "accepted_qty", "rejected_qty", "damaged_qty"].map((k) => (
                            <td key={k} className="px-2 py-1.5 text-right">
                              <input data-testid={`grn-amend-${k}-${l.grn_line_uid}`} type="number" min="0" step="any"
                                className={`${numCls} ${Math.abs(Number(r[k] || 0) - l[k]) > 0.001 ? "border-[#F97316] bg-orange-50" : ""}`}
                                disabled={frozen && k === "accepted_qty"}
                                value={r[k] ?? ""} onChange={(e) => setRow(l.grn_line_uid, { [k]: e.target.value })} />
                            </td>
                          ))}
                          <td className="px-2 py-1.5 text-[10px] text-slate-500">
                            {frozen ? (
                              <span className="inline-flex items-center gap-1 text-slate-600 font-bold">
                                <Lock size={10} /> {l.pi_locked ? "PI exists — accepted frozen" : "Batch reversed"}
                              </span>
                            ) : (
                              <>
                                min accepted <b>{l.min_accepted}</b>
                                {l.consumed_qty > 0 && <> (consumed {l.consumed_qty})</>}
                                {l.reserved_qty > 0 && <> (+{l.reserved_qty} reserved)</>}
                                <br />max accepted <b>{l.max_accepted}</b>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-400 font-bold mb-0.5">Amendment Reason *</div>
                <textarea data-testid="grn-amend-reason" className={`${inputCls} h-16`} value={reason}
                  onChange={(e) => setReason(e.target.value)} placeholder="Why is this correction needed?" />
              </div>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button data-testid="grn-amend-cancel-btn" onClick={onClose}
            className="px-3 py-1.5 text-xs font-bold text-slate-600 border border-slate-300 rounded hover:bg-slate-50">Close</button>
          {ctx.amendable && (
            <button data-testid="grn-amend-submit-btn" onClick={submit}
              disabled={saving || !anyChange || problems.length > 0 || !reason.trim()}
              className="px-3 py-1.5 text-xs font-bold text-white bg-[#F97316] rounded hover:bg-orange-600 disabled:opacity-50">
              {saving ? "Submitting…" : "Submit Amendment for Approval"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
